import React, { useEffect, useMemo, useState } from 'react';
import './LocalizationControlPlane.css';
import { getAIProvider } from '../../domain/localization-control-plane/aiProvider';
import {
  approveReview,
  generateAssistant,
  processFinding,
  processSourceUpdate,
  rejectReview,
} from '../../domain/localization-control-plane/pipeline';
import { pseudolocalize } from '../../domain/localization-control-plane/pseudolocalize';
import { demoScannerFindings, ingestionPolicy, localeDisplayNames, sampleFinding, targetLocales } from '../../domain/localization-control-plane/seed';
import { resetState, loadState, saveState } from '../../domain/localization-control-plane/storage';
import { updateApprovedTerm } from '../../domain/localization-control-plane/terminology';
import { LocaleCode, LocalizationState } from '../../domain/localization-control-plane/types';
import { scoreReadiness } from '../../domain/localization-control-plane/readiness';

type Section = 'overview' | 'static' | 'ingested' | 'assistant' | 'terminology' | 'review' | 'readiness' | 'audit' | 'settings';

const provider = getAIProvider();
const demoPassword = 'TenableDemo';
const passwordSessionKey = 'localization-control-plane-unlocked';

function passwordSessionStorage(): Storage | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

function statusClass(status: string): string {
  return `lcp-status lcp-status-${status.toLowerCase().replace(/_/g, '-')}`;
}

function metric(value: string | number, label: string) {
  return (
    <div className="lcp-metric">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function directionFor(state: LocalizationState, locale: LocaleCode) {
  return state.locales.find((item) => item.code === locale)?.direction ?? 'ltr';
}

function LocalizationControlPlane() {
  const [isUnlocked, setIsUnlocked] = useState(() => (
    passwordSessionStorage()?.getItem(passwordSessionKey) === 'true'
  ));
  const [passwordAttempt, setPasswordAttempt] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [state, setState] = useState<LocalizationState>(() => loadState());
  const [active, setActive] = useState<Section>('overview');
  const [selectedSourceId, setSelectedSourceId] = useState('source_help_remediation');
  const [editedText, setEditedText] = useState('Review remediation guidance before applying changes.');
  const [busy, setBusy] = useState(false);
  const [assistantLocale, setAssistantLocale] = useState<LocaleCode>('ja');
  const [findingLocale, setFindingLocale] = useState<LocaleCode>('fr');
  const [termEdit, setTermEdit] = useState<Record<string, string>>({});

  useEffect(() => saveState(state), [state]);

  const selectedSource = state.sources.find((source) => source.id === selectedSourceId) ?? state.sources[0];
  const localizationsForSource = state.localizations.filter((item) => item.sourceContentId === selectedSource?.id);
  const openReviews = state.reviewItems.filter((item) => item.status === 'OPEN');
  const published = state.localizations.filter((item) => item.status === 'PUBLISHED').length;
  const tmReused = state.localizations.filter((item) => item.tmReused).length;
  const readinessScores = useMemo(() => scoreReadiness(demoScannerFindings), []);

  function unlockDemo(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (passwordAttempt === demoPassword) {
      passwordSessionStorage()?.setItem(passwordSessionKey, 'true');
      setIsUnlocked(true);
      setPasswordAttempt('');
      setPasswordError('');
      return;
    }
    setPasswordError('Incorrect password.');
  }

  async function simulateEnglishUpdate() {
    if (!selectedSource) return;
    setBusy(true);
    const next = await processSourceUpdate(state, selectedSource.id, editedText, provider);
    setState(next);
    setBusy(false);
  }

  async function runFinding() {
    setBusy(true);
    setState(await processFinding(state, provider, findingLocale));
    setBusy(false);
  }

  async function runAssistant() {
    setBusy(true);
    setState(await generateAssistant(state, provider, assistantLocale, 'What should I do about this critical vulnerability?'));
    setBusy(false);
  }

  function approve(reviewId: string, value: string) {
    setState(approveReview(state, reviewId, value));
  }

  function updateTerm(conceptId: string, locale: LocaleCode, value: string) {
    setState({
      ...state,
      terminology: updateApprovedTerm(state.terminology, conceptId, locale, value),
      auditEvents: [{
        id: `audit_${Date.now()}`,
        type: 'TERMINOLOGY_UPDATED',
        lane: 'STATIC_UI',
        locale,
        message: `${conceptId} approved ${locale} term changed and terminology version incremented.`,
        timestamp: new Date().toISOString(),
      }, ...state.auditEvents],
    });
  }

  const nav: Array<[Section, string]> = [
    ['overview', 'Overview'],
    ['static', 'Static UI'],
    ['ingested', 'Ingested Data'],
    ['assistant', 'Hexa / AI'],
    ['terminology', 'Terminology'],
    ['review', 'Review Queue'],
    ['readiness', 'i18n Readiness'],
    ['audit', 'Audit Log'],
    ['settings', 'Settings'],
  ];

  if (!isUnlocked) {
    return (
      <section className="lcp-page">
        <div className="lcp-password-shell">
          <form className="lcp-password-panel" onSubmit={unlockDemo}>
            <p className="lcp-kicker">Private demo</p>
            <h1>Localization Control Plane</h1>
            <p>
              Enter the demo password to view the AI-led localization walkthrough.
            </p>
            <label className="lcp-field">
              Password
              <input
                autoComplete="current-password"
                autoFocus
                type="password"
                value={passwordAttempt}
                onChange={(event) => {
                  setPasswordAttempt(event.target.value);
                  setPasswordError('');
                }}
              />
            </label>
            {passwordError && <p className="lcp-password-error" role="alert">{passwordError}</p>}
            <button className="lcp-primary" type="submit">Enter Demo</button>
          </form>
        </div>
      </section>
    );
  }

  return (
    <section className="lcp-page">
      <header className="lcp-hero">
        <div>
          <p className="lcp-kicker">AI-led Localization Control Plane</p>
          <h1>One control plane, three execution lanes</h1>
          <p>
            A Tenable One-style localization architecture for static UI, ingested exposure data, and Hexa-generated
            content, with governed terminology, quality gates, human exception review, and provenance.
          </p>
        </div>
        <div className="lcp-hero-panel">
          <span>Demo provider</span>
          <strong>{provider.name}</strong>
          <button type="button" onClick={() => setState(resetState())}>Reset Demo Data</button>
        </div>
      </header>

      <nav className="lcp-nav" aria-label="Localization demo sections">
        {nav.map(([id, label]) => (
          <button key={id} type="button" className={active === id ? 'active' : ''} onClick={() => setActive(id)}>
            {label}
          </button>
        ))}
      </nav>

      {active === 'overview' && (
        <div className="lcp-grid">
          <section className="lcp-panel lcp-wide">
            <h2>Control Plane Dashboard</h2>
            <div className="lcp-metrics">
              {metric('6 + en', 'target locales')}
              {metric(state.auditEvents.length, 'localization events')}
              {metric(`${published}/${Math.max(1, state.localizations.length)}`, 'published')}
              {metric(openReviews.length, 'review-required')}
              {metric(state.reviewItems.filter((item) => item.terminologyIssues.length > 0).length, 'terminology violations')}
              {metric(tmReused, 'TM reuse count')}
              {metric('3 seeded', 'mixed-language findings')}
              {metric(`${Math.round(readinessScores.reduce((sum, score) => sum + score.externalized, 0) / readinessScores.length)}%`, 'i18n readiness')}
            </div>
            <div className="lcp-pipeline" aria-label="Localization pipeline visualization">
              {['SOURCE CHANGE', 'CONTEXT', 'TERMINOLOGY', 'TRANSLATION', 'DETERMINISTIC QA', 'SEMANTIC QE', 'RISK/POLICY', 'PUBLISH / REVIEW'].map((stage) => (
                <span key={stage} className={state.pipelineStage === stage ? 'active' : ''}>{stage}</span>
              ))}
            </div>
          </section>
          <section className="lcp-panel">
            <h2>Execution Lanes</h2>
            <div className="lcp-lanes">
              <button type="button" onClick={() => setActive('static')}><strong>Static UI</strong><span>source events to localized artifacts</span></button>
              <button type="button" onClick={() => setActive('ingested')}><strong>Ingested Data</strong><span>presentation enrichment, source preserved</span></button>
              <button type="button" onClick={() => setActive('assistant')}><strong>Hexa / AI</strong><span>locale-native governed generation</span></button>
            </div>
          </section>
          <section className="lcp-panel">
            <h2>Recent Activity</h2>
            <Activity events={state.auditEvents.slice(0, 8)} />
          </section>
        </div>
      )}

      {active === 'static' && selectedSource && (
        <div className="lcp-grid">
          <section className="lcp-panel">
            <h2>English Source Strings</h2>
            <div className="lcp-source-list">
              {state.sources.map((source) => (
                <button
                  key={source.id}
                  type="button"
                  className={source.id === selectedSource.id ? 'selected' : ''}
                  onClick={() => {
                    setSelectedSourceId(source.id);
                    setEditedText(source.sourceText);
                  }}
                >
                  <strong>{source.key}</strong>
                  <span>{source.sourceText}</span>
                  <small>{source.riskLevel} - {source.component}</small>
                </button>
              ))}
            </div>
          </section>
          <section className="lcp-panel lcp-wide">
            <h2>Simulate English Update</h2>
            <label className="lcp-field">
              English authoritative source
              <textarea value={editedText} onChange={(event) => setEditedText(event.target.value)} />
            </label>
            <p className="lcp-note">{selectedSource.context}</p>
            <button className="lcp-primary" type="button" onClick={simulateEnglishUpdate} disabled={busy}>
              {busy ? 'Processing...' : 'Simulate English Update'}
            </button>
            <div className="lcp-results">
              {targetLocales.map((locale) => {
                const localization = localizationsForSource.find((item) => item.locale === locale);
                const quality = localization ? state.qualityResults.find((item) => item.id === localization.qualityResultId) : undefined;
                return (
                  <article className="lcp-result" key={locale} dir={directionFor(state, locale)}>
                    <header>
                      <strong>{localeDisplayNames[locale]}</strong>
                      <span className={statusClass(localization?.status ?? 'PENDING')}>{localization?.status ?? 'PENDING'}</span>
                    </header>
                    <p>{localization?.localizedText ?? 'Waiting for source event.'}</p>
                    <small>{localization?.tmReused ? 'TM reused' : localization ? `${localization.model} - score ${localization.qualityScore}` : 'No candidate yet'}</small>
                    {quality && (
                      <ul>
                        {quality.deterministicChecks.filter((check) => !check.passed).map((check) => (
                          <li key={`${check.name}-${check.message}`}>{check.message ?? check.name}</li>
                        ))}
                      </ul>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        </div>
      )}

      {active === 'ingested' && (
        <div className="lcp-grid">
          <section className="lcp-panel">
            <h2>Original Finding Payload</h2>
            <pre>{JSON.stringify(sampleFinding, null, 2)}</pre>
            <label className="lcp-field">
              Locale
              <select value={findingLocale} onChange={(event) => setFindingLocale(event.target.value as LocaleCode)}>
                {targetLocales.map((locale) => <option value={locale} key={locale}>{localeDisplayNames[locale]}</option>)}
              </select>
            </label>
            <button className="lcp-primary" type="button" onClick={runFinding} disabled={busy}>Simulate Incoming Finding</button>
          </section>
          <section className="lcp-panel">
            <h2>Schema-Driven Policy</h2>
            <table>
              <tbody>
                {ingestionPolicy.map((policy) => (
                  <tr key={policy.field}><th>{policy.field}</th><td>{policy.policy}</td><td>{policy.rationale}</td></tr>
                ))}
              </tbody>
            </table>
          </section>
          <section className="lcp-panel lcp-wide">
            <h2>Localized Presentation Enrichment</h2>
            <div className="lcp-table-list">
              {state.ingestedResults.map((result) => (
                <article key={result.field}>
                  <strong>{result.field}</strong>
                  <span>{result.originalValue}</span>
                  <span dir={directionFor(state, result.locale)}>{result.localizedValue}</span>
                  <small>{result.action} - {result.provenance}</small>
                </article>
              ))}
            </div>
          </section>
        </div>
      )}

      {active === 'assistant' && (
        <div className="lcp-grid">
          <section className="lcp-panel">
            <h2>Hexa / Security Assistant</h2>
            <p className="lcp-note">
              Hexa-style content should be generated in the user locale, not translated after the fact. The control plane
              still injects terminology and validates stable security entities separately from prose.
            </p>
            <label className="lcp-field">
              Locale
              <select value={assistantLocale} onChange={(event) => setAssistantLocale(event.target.value as LocaleCode)}>
                {targetLocales.map((locale) => <option value={locale} key={locale}>{localeDisplayNames[locale]}</option>)}
              </select>
            </label>
            <label className="lcp-field">
              Question
              <textarea readOnly value="What should I do about this critical vulnerability?" />
            </label>
            <button className="lcp-primary" type="button" onClick={runAssistant} disabled={busy}>Generate Locale-Native Hexa Response</button>
          </section>
          <section className="lcp-panel lcp-wide">
            <h2>Generated Output</h2>
            {state.assistantResults.map((result) => (
              <article className="lcp-assistant-output" key={result.locale} dir={directionFor(state, result.locale)}>
                <strong>{localeDisplayNames[result.locale]}</strong>
                <p>{result.response}</p>
                <small>{result.provenance}</small>
                <ul>
                  {result.validation.map((check) => <li key={check.name}>{check.name}: {check.passed ? 'passed' : check.message}</li>)}
                </ul>
              </article>
            ))}
          </section>
        </div>
      )}

      {active === 'terminology' && (
        <section className="lcp-panel">
          <h2>Terminology Registry</h2>
          <div className="lcp-term-grid">
            {state.terminology.map((concept) => (
              <article key={concept.conceptId} className="lcp-term">
                <header>
                  <strong>{concept.canonicalEnglish}</strong>
                  <span>v{concept.version} - {concept.status}</span>
                </header>
                <p>{concept.definition}</p>
                <small>{concept.conceptId} - owner: {concept.owner}</small>
                <div className="lcp-term-table">
                  {concept.terms.map((term) => {
                    const editKey = `${concept.conceptId}-${term.locale}`;
                    return (
                      <label key={editKey}>
                        <span>{term.locale}</span>
                        <input value={termEdit[editKey] ?? term.approved} onChange={(event) => setTermEdit({ ...termEdit, [editKey]: event.target.value })} />
                        <button type="button" onClick={() => updateTerm(concept.conceptId, term.locale, termEdit[editKey] ?? term.approved)}>Save</button>
                        <small>forbidden: {term.forbidden.join(', ') || 'none'}</small>
                      </label>
                    );
                  })}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {active === 'review' && (
        <section className="lcp-panel">
          <h2>Human Exception Queue</h2>
          <div className="lcp-review-list">
            {state.reviewItems.map((review) => (
              <article key={review.id} className={review.status !== 'OPEN' ? 'resolved' : ''}>
                <header>
                  <strong>{localeDisplayNames[review.locale]}</strong>
                  <span className={statusClass(review.status)}>{review.status}</span>
                </header>
                <p><b>Source:</b> {review.sourceText}</p>
                <ReviewEditor
                  value={review.finalApprovedValue ?? review.candidateTranslation}
                  disabled={review.status !== 'OPEN'}
                  onApprove={(value) => approve(review.id, value)}
                  onReject={() => setState(rejectReview(state, review.id))}
                />
                <small>{review.severity} - {review.reason}</small>
                {review.terminologyIssues.map((issue) => <p className="lcp-issue" key={issue}>{issue}</p>)}
              </article>
            ))}
            {state.reviewItems.length === 0 && <p>No review items yet. Run the static UI demo to create high-risk and terminology exceptions.</p>}
          </div>
        </section>
      )}

      {active === 'readiness' && (
        <div className="lcp-grid">
          <section className="lcp-panel lcp-wide">
            <h2>Closing Today&apos;s Mixed-Language Gap</h2>
            <p className="lcp-note">
              The scanner represents the first migration workstream: identify redesigned components that are already
              Japanese/French-localized, components still English-only, and code paths where strings are not externalized.
              These findings become engineering backlog items, not manual translation tickets.
            </p>
          </section>
          <section className="lcp-panel">
            <h2>Repository Scanner Demo</h2>
            <div className="lcp-table-list">
              {demoScannerFindings.map((finding) => (
                <article key={`${finding.file}-${finding.line}`}>
                  <strong>{finding.component}</strong>
                  <span>{finding.text}</span>
                  <small>{finding.file}:{finding.line} - {finding.classification}</small>
                </article>
              ))}
            </div>
          </section>
          <section className="lcp-panel">
            <h2>Readiness Score</h2>
            <table>
              <thead><tr><th>Component</th><th>Externalized</th><th>RTL</th><th>Pseudo</th><th>Status</th></tr></thead>
              <tbody>
                {readinessScores.map((score) => (
                  <tr key={score.component}><th>{score.component}</th><td>{score.externalized}%</td><td>{score.rtlReady ? 'Yes' : 'No'}</td><td>{score.pseudoLocalized ? 'Yes' : 'No'}</td><td>{score.status}</td></tr>
                ))}
              </tbody>
            </table>
          </section>
          <section className="lcp-panel lcp-wide">
            <h2>Pseudolocalized Product Preview</h2>
            <div className="lcp-product-preview">
              {state.sources.slice(0, 5).map((source) => <button type="button" key={source.id}>{pseudolocalize(source.sourceText)}</button>)}
            </div>
          </section>
        </div>
      )}

      {active === 'audit' && (
        <section className="lcp-panel">
          <h2>Audit / Events</h2>
          <Activity events={state.auditEvents} />
        </section>
      )}

      {active === 'settings' && (
        <section className="lcp-panel">
          <h2>Settings</h2>
          <p>Provider: {provider.name}. Configure `REACT_APP_AI_PROVIDER=mock|openai`; mock is the guaranteed demo fallback.</p>
          <table>
            <thead><tr><th>Locale</th><th>Direction</th><th>Threshold</th><th>Auto-publish</th></tr></thead>
            <tbody>
              {state.locales.map((locale) => <tr key={locale.code}><th>{locale.displayName}</th><td>{locale.direction}</td><td>{locale.qualityThreshold}</td><td>{locale.autoPublishEnabled ? 'Enabled' : 'Disabled'}</td></tr>)}
            </tbody>
          </table>
        </section>
      )}
    </section>
  );
}

function Activity({ events }: { events: LocalizationState['auditEvents'] }) {
  return (
    <ol className="lcp-activity">
      {events.map((event) => (
        <li key={event.id}>
          <time>{new Date(event.timestamp).toLocaleTimeString()}</time>
          <strong>{event.type}</strong>
          <span>{event.locale ? `${event.locale} - ` : ''}{event.message}</span>
        </li>
      ))}
      {events.length === 0 && <li><span>No events yet. Run a demo workflow to populate the lifecycle history.</span></li>}
    </ol>
  );
}

function ReviewEditor({
  value,
  disabled,
  onApprove,
  onReject,
}: {
  value: string;
  disabled: boolean;
  onApprove: (value: string) => void;
  onReject: () => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <div className="lcp-review-editor">
      <textarea value={draft} disabled={disabled} onChange={(event) => setDraft(event.target.value)} />
      <div>
        <button type="button" disabled={disabled} onClick={() => onApprove(draft)}>Approve</button>
        <button type="button" disabled={disabled} onClick={onReject}>Reject</button>
      </div>
    </div>
  );
}

export default LocalizationControlPlane;
